import React from 'react';
import DialogDataComponent from 'Core/components/DialogDataComponent';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {getGlobalActions} from 'Core/helpers/redux';
import * as actions from './actions';
import {reducerStoreKey} from './reducer';
import {selectors} from 'Core/store/reducers';
import {areAllObjectPropsEmpty, getArray, getNumber, isset} from 'Core/helpers/data';
import DataTable, {DATA_TABLE_CELL_TYPE} from 'Core/components/advanced/DataTable';
import {
	DataTableCellDateTypeOptionsDataObject,
	DataTableCellTextTypeOptionsDataObject
} from 'Core/components/advanced/DataTable/DataTableCell/dataObjects';
import {PAGINATION_TYPE} from 'Core/components/action/Pagination';
import {getAppLocaleDateFormat} from 'Core/helpers/locale';
import {LOCALE_DATE_FORMAT_NAME} from 'Core/const/locale';
import {BUTTON_STYLE} from 'Core/components/display/Button';
import {
	icon_font_close_symbol,
	icon_font_create_symbol,
	icon_font_save_symbol
} from 'Config/app';
import {isMacintosh} from 'Core/helpers/system';
import * as filterDataMap from './dataMap/filter';
import SimpleStaticSearch, {
	SIMPLE_STATIC_SEARCH_DISPLAY_TYPE,
	SIMPLE_STATIC_SEARCH_LAYOUT,
	SimpleStaticSearchOptionObject
} from 'Core/components/advanced/SimpleStaticSearch';
import {STANDARD_DATE_TIME_FORMAT} from 'Core/const/datetime';
import PhoneInput from 'Core/components/input/PhoneInput';
import CustomerCustomFieldFilter from 'Pages/apps/default/customers/components/CustomerCustomFieldFilter';
import {FORM_FIELD_LABEL_POSITION} from 'Core/components/advanced/FormWrapper/FormField';
import Label from 'Core/components/display/Label';
import {fetchCustomerCustomFieldsAction} from 'Components/advanced/ImportCustomersFromList/actions';
import NumberLabel from 'Core/components/display/NumberLabel';
import {cloneDeep} from 'lodash';

/**
 * Redux 'mapStateToProps' function
 *
 * @param {object} state - Redux entire store state.
 * @return {Object<string, any>} Mapped props that can be used in component.
 */
const mapStateToProps = state => ({
	mainList: selectors[reducerStoreKey].getCustomersSegmentForImport(state),
	mainListPagination: selectors[reducerStoreKey].getCustomersSegmentPaginationForImport(state),
	mainListSort: selectors[reducerStoreKey].getCustomersSegmentSortForImport(state),
	mainListFilter: selectors[reducerStoreKey].getCustomersSegmentFilterForImport(state),
});

class CustomerSegmentPreviewDialog extends DialogDataComponent {
	constructor(props) {
		super(props, {
			data: {
				/**
				 * Currently selected search filter
				 */
				filter: [],
				/**
				 * Flag showing if filter is loading
				 */
				filterLoading: false,

				/**
				 * Flag showing if page is loading data
				 * @type {boolean}
				 */
				loading: false,
			},

			/**
			 * Customer custom fields
			 * @type {
			 * 	{id: number, name: string, code: string, description: string, type: CustomerCustomFiledType}[]
			 * 	|null|undefined
			 * }
			 */
			customerCustomFields: null,
		}, {
			translationPath: 'CustomerSegmentPreviewDialog',
			disableLoad: true,
			
			// Custom component options
			/**
			 * Flag that specifies if filter component will be visible
			 * @note Component will be rendered, only 'no-display' css class will be used.
			 */
			hideFilter: true,
		});

		// Refs
		this.mainListFilterRef = null;

		// Data methods
		this.reloadMainList = this.reloadMainList.bind(this);
		this.loadMainListPage = this.loadMainListPage.bind(this);
		this.sortMainList = this.sortMainList.bind(this);
		this.filterMainList = this.filterMainList.bind(this);
		this.removeMainListFilter = this.removeMainListFilter.bind(this);
		this.isMainListFilterEmpty = this.isMainListFilterEmpty.bind(this);
	}


	async asyncComponentDidMount(override = false) {
		await super.asyncComponentDidMount(override);
		
		const {fetchCustomerCustomFieldsAction} = this.props;
		
		await this.loadMainListPage();

		// Load customer custom fields for search
		await this.executeAbortableActionMount(fetchCustomerCustomFieldsAction)
			// Load customer custom field into local state
			.then(ccf => this.setState({customerCustomFields: ccf}));
	}
	
	componentWillUnmount() {
		super.componentWillUnmount();
		
		const {resetCustomersSegmentAction} = this.props;
		resetCustomersSegmentAction();
	}


	// Data methods -----------------------------------------------------------------------------------------------------
	/** @inheritDoc */
	getDataToReturn() {
		return cloneDeep(getArray(this.props, 'mainList'));
	}

	/**
	 * Reload main list using current options (page, sort, ...)
	 *
	 * @param {boolean} [loadingOverlay=true] - Flag that specifies if loading overlay should be visible.
	 * @return {Promise<*>}
	 */
	reloadMainList(loadingOverlay = true) {
		const {
			loadCustomersSegmentAction, projectId, conditions, mainListPagination, mainListSort, mainListFilter
		} = this.props;
		const {pageNo, perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(mainListFilter);
		
		return this.executeAbortableAction(
			loadCustomersSegmentAction, projectId, conditions, oFilter, pageNo, perPage, sortBy, sortDir, loadingOverlay
		)
			.then(res => {
				this.mainListFilterRef?.reload();
				return res;
			});
	}

	/**
	 * Reload main list using current options (page, sort, ...) if any
	 * @param {number} [pageNo=1] - Page number to load (starts with 1).
	 * @return {Promise<*>}
	 */
	loadMainListPage(pageNo = 1) {
		const {
			loadCustomersSegmentAction, projectId, conditions, mainListPagination, mainListSort, mainListFilter
		} = this.props;
		const {perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(mainListFilter);

		return this.executeAbortableAction(
			loadCustomersSegmentAction, projectId, conditions, oFilter, pageNo, perPage, sortBy, sortDir
		);
	}

	/**
	 * Sort main list
	 * @param {string} sortBy - Name of the sort column.
	 * @param {string} sortDir - Direction of the sort.
	 * @return {Promise<*>}
	 */
	sortMainList(sortBy, sortDir) {
		const {loadCustomersSegmentAction, projectId, conditions, mainListPagination, mainListFilter} = this.props;
		const {pageNo, perPage} = mainListPagination;
		const oFilter = filterDataMap.output(mainListFilter);

		return this.executeAbortableAction(
			loadCustomersSegmentAction, projectId, conditions, oFilter, pageNo, perPage, sortBy, sortDir
		);
	}

	/**
	 * Filter main list
	 * @param {Object} filter - Filter object where keys are filter field names and values are filter values.
	 * @return {Promise<*>}
	 */
	filterMainList(filter) {
		const {
			mainListPagination, mainListSort, projectId, conditions, loadCustomersSegmentAction, 
			setCustomersSegmentFilterAction,
		} = this.props;
		const {perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(filter);

		// Set filter so that the change in the filter will be detected after IO
		setCustomersSegmentFilterAction(oFilter);
		
		return this.executeAbortableAction(
			loadCustomersSegmentAction, projectId, conditions, oFilter, 1, perPage, sortBy, sortDir
		);
	}

	/**
	 * Remove main list filter
	 * @return {Promise<*>}
	 */
	removeMainListFilter() {
		const {resetCustomersSegmentAction} = this.props;

		// Clear main list and filter
		resetCustomersSegmentAction();
	}

	/**
	 * Check if main list filter is applied
	 * @return {Boolean}
	 */
	isMainListFilterEmpty() {
		return areAllObjectPropsEmpty(this.getProp('mainListFilter'), true, false);
	}
	
	
	// Render methods ---------------------------------------------------------------------------------------------------
	/** @inheritDoc */
	renderDataActionButtons({
		saveLabel = undefined, saveIcon = undefined, closeLabel = undefined, closeIcon = undefined,
	} = {}, hideClose = false) {
		const {showSubmitButton} = this.props;

		let buttons = [];

		// Close/cancel button
		if (isMacintosh() && !hideClose) buttons.push({
			style: BUTTON_STYLE.DEFAULT,
			label: (closeLabel ? closeLabel : this.t('Close', 'general')),
			icon: (isset(closeIcon) && closeLabel !== null ? closeIcon : icon_font_close_symbol),
			onClick: this.close,
		});
		
		if (showSubmitButton) {
			buttons.push({
				style: BUTTON_STYLE.HIGHLIGHT,
				label: (saveLabel ? saveLabel : this.t('Save', 'general')),
				icon: (isset(saveIcon) && saveIcon !== null ? saveIcon : icon_font_save_symbol),
				onClick: this.save,
				autofocus: true,
				disabled: getArray(this.props, 'mainList').length === 0
			});
		}

		// Close/cancel button
		if (!isMacintosh() && !hideClose) buttons.push({
			style: BUTTON_STYLE.DEFAULT,
			label: (closeLabel ? closeLabel : this.t('Close', 'general')),
			icon: (isset(closeIcon) && closeLabel !== null ? closeIcon : icon_font_close_symbol),
			onClick: this.close,
		});

		return this.renderActionButtons(buttons);
	}
	
	render() {
		const {mainList, mainListPagination, mainListSort, mainListFilter} = this.props;
		const {customerCustomFields} = this.state;
		
		return this.renderDialog(
			this.renderTitle(
				<>
					<Label content={this.t('title')} /> (<NumberLabel 
					number={getNumber(mainListPagination, 'totalRows')} />)
				</>
			),
			(
				<>
					<SimpleStaticSearch
						styleName="compact"
						className={`main-search rows-4 ${this.getOption('hideFilter') ? 'no-display' : ''}`}
						defaultCollapse={true}
						layout={SIMPLE_STATIC_SEARCH_LAYOUT.STACKED}
						buttonProps={{
							displayStyle: BUTTON_STYLE.DEFAULT
						}}
						options={[
							new SimpleStaticSearchOptionObject(
								'externalCrmId',
								this.t('crmIdField')
							),
							new SimpleStaticSearchOptionObject(
								'firstName',
								this.t('firstNameField')
							),
							new SimpleStaticSearchOptionObject(
								'lastName',
								this.t('lastNameField')
							),
							new SimpleStaticSearchOptionObject(
								'dateOfBirth',
								this.t('dateOfBirthField'),
								SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.DATE,
								{
									valueFormat: STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S
								}
							),
							new SimpleStaticSearchOptionObject(
								'mobilePhoneNumber',
								this.t('phoneField'),
								SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.CUSTOM,
								{disableDropdown: false},
								PhoneInput,
								undefined,
								''
							),
							new SimpleStaticSearchOptionObject(
								'email',
								this.t('emailField')
							),
							new SimpleStaticSearchOptionObject(
								'customerCustomFields',
								'',
								SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.CUSTOM,
								{options: getArray(customerCustomFields).map(i => ({label: i.name, value: i.name}))},
								CustomerCustomFieldFilter,
								{
									labelPosition: FORM_FIELD_LABEL_POSITION.NONE, 
									inputClassName: 'customer-custom-field-item custom-field-item'
								}
							),
						]}
						value={mainListFilter}
						title={(<Label icon="search" content={this.t('Search', 'general')} />)}
						applied={!this.isMainListFilterEmpty()}
						enableToolbar={true}
						enableResetButton={false}
						onChange={this.filterMainList}
						onRemove={this.removeMainListFilter}
						ref={node => { this.mainListFilterRef = node; }}
					/>
					
					<DataTable
						id="customers-segment-preview-table"
						className="standard"
						responsive={true}
						highlightOnHover={true}
						columns={[
							{
								name: 'crmId',
								label: this.t('crmIdField'),
								dataTypeOptions: new DataTableCellTextTypeOptionsDataObject({
									whiteSpace: 'nowrap',
								}),
								defaultValue: '—',
								width: 1,
							},
							{
								name: 'firstName',
								label: this.t('firstNameField')
							},
							{
								name: 'lastName',
								label: this.t('lastNameField'),
							},
							{
								name: 'dateOfBirth',
								label: this.t('dateOfBirthField'),
								dataType: DATA_TABLE_CELL_TYPE.DATE,
								dataTypeOptions: new DataTableCellDateTypeOptionsDataObject({
									outputFormat: getAppLocaleDateFormat(LOCALE_DATE_FORMAT_NAME.SHORT),
									whiteSpace: 'nowrap',
								}),
								defaultValue: '—',
							},
							{
								name: 'phone',
								label: this.t('phoneField'),
								defaultValue: '—',
							},
							{
								name: 'email',
								label: this.t('emailField'),
								defaultValue: '—',
							},
						]}
						data={mainList}
						paginationType={PAGINATION_TYPE.STATIC}
						onSortByColumn={this.sortMainList}
						onPaginationClick={this.loadMainListPage}
						{...mainListPagination}
						{...mainListSort}
					/>
				</>
			), {
				saveLabel: this.t('add_btn'), 
				saveIcon: icon_font_create_symbol,
			}
		);
	}
}

/**
 * Define component's own props that can be passed to it by parent components
 */
CustomerSegmentPreviewDialog.propTypes = {
	...DialogDataComponent.propTypes,
	
	// ID of the project since customers are assigned to projects
	projectId: PropTypes.string.isRequired,
	// Customer segment conditions to preview
	// @type {ConditionListItemDataObject[]}
	conditions: PropTypes.array,
	// Flag showing if submit button should be visible
	// @type {boolean}
	showSubmitButton: PropTypes.bool,
};

/**
 * Define the component's own props that can be passed to it by parent components
 */
CustomerSegmentPreviewDialog.defaultProps = {
	showSubmitButton: true,
};

export default connect(
	mapStateToProps, getGlobalActions(actions, {fetchCustomerCustomFieldsAction})
)(CustomerSegmentPreviewDialog);